import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Tabs, Tab, Button, Avatar } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import WelcomeMessageTemplate from '../message-create-view/WelcomeMessageTemplate';
import WelcomeMessageDetails from '../message-create-view/WelcomeMessageDetails';
import Audience from '../message-create-view/Audience';
import { useDispatch, useSelector } from 'react-redux';
import {
  createNewWelcomeMessage,
  updateExistingWelcomeMessage,
} from '../../../../utils/ServicesAPIUtils';
import { notificationManager } from '../../../../utils/NotificationUtils';
import { decryptLocalStorage } from '../../../../utils/authUtils';
import { danger, formDataValidate, success } from '../../../../utils/formUtils';
import {
  allFloorsSelector,
  allRoomsSelector,
  allRoomTypesSelector,
  fetchAllAccommodations,
} from '../../../property/propertySlice';
import undoIcon from '../../../../assets/img/Undo.svg';
import redoIcon from '../../../../assets/img/Redo.svg';

const drawerWidths = {
  default: '1000px',
  template: '85vw',
};

const MessageCreateView = ({ message, onClose, onTabChange, onSave, language, isCreate }) => {
  const [tabValue, setTabValue] = useState(0);
  const [drawerWidth, setDrawerWidth] = useState(drawerWidths.default);
  const [templateEditing, setTemplateEditing] = useState(isCreate);
  const [isTabInValid, setIsTabInValid] = useState(true);
  const [isTemplateSaved, setIsTemplateSaved] = useState(!isCreate);
  const originalRoomList = useSelector(allRoomsSelector);
  const originalRoomTypeList = useSelector(allRoomTypesSelector);
  const originalFloorList = useSelector(allFloorsSelector);
  const hotelId = useSelector((state) => state.property.selectedHotel);
  const lang = decryptLocalStorage('lang') !== 'en' ? decryptLocalStorage('lang') : '';
  const templateRef = useRef();
  const dispatch = useDispatch();

  const parseConfirmationIds = (confirmationIds) => {
    try {
      return JSON.parse(confirmationIds || '[]');
    } catch (e) {
      console.warn('Invalid confirmationIds format:', confirmationIds);
      return [];
    }
  };

  const [formData, setFormData] = useState({
    title: {
      value: message?.title || '',
      fieldName: 'Title',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    device: {
      value: message?.deviceTypes || [],
      fieldName: 'Select Device',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    isActive: {
      value: message?.isActive ?? true,
      fieldName: 'Active',
      errors: {},
      validations: [],
      tab: 0,
    },
    template: {
      value:
        typeof message?.welcomeMessageTemplate === 'string'
          ? JSON.parse(message.welcomeMessageTemplate)
          : message?.welcomeMessageTemplate || '',
      fieldName: 'Template',
      errors: {},
      validations: [],
      tab: 1,
    },
    audience: {
      value: message?.audienceType || 'All',
      fieldName: 'Audience',
      errors: {},
      validations: [],
      tab: 2,
    },
    audienceType: {
      value:
        message?.audienceType === 'Custom'
          ? Object.keys(message?.audienceCriteria || {})
              .filter((key) => {
                const criteria = message.audienceCriteria[key];
                return criteria && Array.isArray(criteria.values) && criteria.values.length > 0;
              })
              .map((key) => {
                switch (key) {
                  case 'rooms':
                    return 'Rooms';
                  case 'roomTypes':
                    return 'Room Type';
                  case 'floors':
                    return 'Floors';
                  case 'blockCodes':
                    return 'Block Code';
                  case 'loyaltyTiers':
                    return 'Loyalty Tier';
                  default:
                    return '';
                }
              })
              .filter(Boolean)
          : ['All'],
      fieldName: 'Select Audience Type(s)',
      errors: {},
      validations: [],
      tab: 2,
    },
    residentRoom: {
      value: message?.audienceCriteria?.rooms?.values || [],
      fieldName: 'Select Room(s)',
      errors: {},
      validations: [],
      tab: 2,
    },
    roomType: {
      value: message?.audienceCriteria?.roomTypes?.values || [],
      fieldName: 'Select Room Type(s)',
      errors: {},
      validations: [],
      tab: 2,
    },
    residentFloor: {
      value: message?.audienceCriteria?.floors?.values || [],
      fieldName: 'Select Floor(s)',
      errors: {},
      validations: [],
      tab: 2,
    },
    blockCodes: {
      value: message?.audienceCriteria?.blockCodes?.values || [],
      fieldName: 'Add Block Codes',
      errors: {},
      validations: [],
      tab: 2,
    },
    loyaltyTiers: {
      value: message?.audienceCriteria?.loyaltyTiers?.values || [],
      fieldName: 'Add Loyalty Tiers',
      errors: {},
      validations: [],
      tab: 2,
    },
    audienceRelations: {
      value: message?.audienceCriteria
        ? {
            Rooms: message.audienceCriteria.rooms?.logic || 'AND',
            RoomType: message.audienceCriteria.roomTypes?.logic || 'AND',
            Floors: message.audienceCriteria.floors?.logic || 'AND',
            BlockCode: message.audienceCriteria.blockCodes?.logic || 'AND',
            LoyaltyTier: message.audienceCriteria.loyaltyTiers?.logic || 'AND',
          }
        : {},
      fieldName: 'Audience Relations',
      errors: {},
      validations: [],
      tab: 2,
    },
    confirmationId: {
      value: parseConfirmationIds(message?.confirmationIds).join(',') || '',
      fieldName: 'Confirmation ID',
      errors: {},
      validations: [],
      tab: 2,
    },
  });
  useEffect(() => {
    if (tabValue === 1) {
      setIsTabInValid(false);
    } else if (tabValue === 0) {
      const detailsFields = Object.fromEntries(
        Object.entries(formData).filter(([, f]) => f.tab === 0),
      );
      const isValid = formDataValidate(detailsFields, false);
      setIsTabInValid(!isValid);
    } else {
      let hasErrors = false;
      if (formData.audience.value === 'Custom') {
        const hasConfirmationId = !!(formData?.confirmationId?.value && formData.confirmationId.value.trim());
        const hasAudienceType = !!((formData?.audienceType?.value || []).filter(v => v !== 'All').length > 0);
        formData.confirmationId.validations = (!hasConfirmationId && !hasAudienceType) ? ['required'] : [];
        formData.audienceType.validations = (!hasConfirmationId && !hasAudienceType) ? ['required'] : [];

        const criteriaFields = {
          'Block Code': 'blockCodes',
          'Loyalty Tier': 'loyaltyTiers',
          Floors: 'residentFloor',
          'Room Type': 'roomType',
          Rooms: 'residentRoom',
        };

        Object.entries(criteriaFields).forEach(([typeLabel, fieldKey]) => {
          if (
            formData?.audienceType?.value?.includes(typeLabel) &&
            (!formData?.[fieldKey]?.value || formData?.[fieldKey]?.value?.length === 0)
          ) {
            formData[fieldKey].validations = ['required'];
          } else {
            formData[fieldKey].validations = [];
          }
        });

        if (!hasConfirmationId && !hasAudienceType) {
          hasErrors = true;
        }

        Object.entries(criteriaFields).forEach(([typeLabel, fieldKey]) => {
          if (
            formData?.audienceType?.value?.includes(typeLabel) &&
            (!formData?.[fieldKey]?.value || formData?.[fieldKey]?.value?.length === 0)
          ) {
            hasErrors = true;
          }
        });
      } else {
        [
          'audienceType',
          'blockCodes',
          'loyaltyTiers',
          'residentFloor',
          'roomType',
          'residentRoom',
        ].forEach((fieldKey) => {
          formData[fieldKey].validations = [];
        });
        formData.confirmationId.validations = [];
      }

      if (
        formData.confirmationId &&
        formData.confirmationId.errors &&
        Object.keys(formData.confirmationId.errors).length > 0
      ) {
        hasErrors = true;
      }

      if (formData.confirmationId && formData.confirmationId.value) {
        if (/[^a-zA-Z0-9,\-]/.test(formData.confirmationId.value)) {
          hasErrors = true;
        }
        if (/,{2,}/.test(formData.confirmationId.value)) {
          hasErrors = true;
        }
        if (/,$/.test(formData.confirmationId.value.trim())) {
          hasErrors = true;
        }
        const segments = formData.confirmationId.value.split(',').map(s => s.trim()).filter(s => s);
        if (segments.length > 0) {
          const uniqueIds = new Set(segments);
          if (segments.length !== uniqueIds.size) {
            hasErrors = true;
          }
        }
      }

      setIsTabInValid(hasErrors);
    }
  }, [
    tabValue,
    formData.title?.value,
    formData.device?.value,
    formData.audience.value,
    formData.audienceType?.value,
    formData.residentRoom?.value,
    formData.roomType?.value,
    formData.residentFloor?.value,
    formData.blockCodes?.value,
    formData.loyaltyTiers?.value,
    formData.confirmationId?.errors,
    formData.confirmationId?.value,
  ]);

  const handleDetailFieldChange = (updatedFields) => {
    setFormData((prev) => {
      const next = { ...prev };
      Object.entries(updatedFields).forEach(([fieldKey, fieldObj]) => {
        next[fieldKey] = {
          ...prev[fieldKey],
          ...fieldObj,
        };
      });
      return next;
    });
  };

  const updateDrawerWidth = (tabIndex) => {
    const newWidth = tabIndex === 1 ? drawerWidths.template : drawerWidths.default;
    setDrawerWidth(newWidth);

    if (onTabChange) {
      onTabChange({
        tabIndex,
        width: newWidth,
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    if (!isCreate && templateEditing && newValue !== tabValue) {
      return;
    }
    setTabValue(newValue);
    updateDrawerWidth(newValue);
    setTemplateEditing(isCreate);
  };

  useEffect(() => {
    updateDrawerWidth(tabValue);
  }, []);

  useEffect(() => {
    if (tabValue === 2 && formData?.audience?.value === 'Custom' && hotelId) {
      dispatch(fetchAllAccommodations(hotelId));
    }
  }, [tabValue, formData?.audience?.value, hotelId, dispatch]);


  const handleInputChange = (value, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]:
        fieldName === 'confirmationId'
          ? {
              ...prev[fieldName],
              value: value,
            }
          : {
              ...prev[fieldName],
              value: value,
            },
    }));
  };

  const handleEditSave = async (template) => {
    if ((isCreate && tabValue === 2) || (!isCreate && templateEditing)) {
      try {
        const audienceCriteria =
          formData.audience.value === 'Custom'
            ? {
                rooms: {
                  logic: formData.audienceRelations.value?.Rooms || 'AND',
                  values: formData.residentRoom.value || [],
                },
                roomTypes: {
                  logic: formData.audienceRelations.value?.RoomType || 'AND',
                  values: formData.roomType.value || [],
                },
                floors: {
                  logic: formData.audienceRelations.value?.Floors || 'AND',
                  values: formData.residentFloor.value || [],
                },
                blockCodes: {
                  logic: formData.audienceRelations.value?.BlockCode || 'AND',
                  values: formData.blockCodes.value || [],
                },
                loyaltyTiers: {
                  logic: 'AND',
                  values: formData.loyaltyTiers.value || [],
                },
              }
            : null;

        const payload = {
          id: isCreate ? undefined : message.id,
          hotelId,
          title: formData.title.value,
          deviceTypes: formData.device.value,
          welcomeMessageTemplate: template?.templateBody ? template : formData.template.value,
          confirmationIds: formData.audience.value === 'Custom' && formData.confirmationId.value ? JSON.stringify(formData.confirmationId.value.split(',').map(id => id.trim()).filter(id => id)) : JSON.stringify([]),
          isActive: formData.isActive.value,
          audienceType: formData.audience.value,
          audienceCriteria: audienceCriteria,
        };
        const response = isCreate
          ? await createNewWelcomeMessage(payload, hotelId, lang)
          : await updateExistingWelcomeMessage(payload, hotelId, lang);

        const updated =
          response?.data?.updateWelcomeMessage || response?.data?.createWelcomeMessage;

        if (updated) {
          if (formData.audience.value !== 'Custom') {
            setFormData((prev) => ({
              ...prev,
              confirmationId: { ...prev.confirmationId, value: '', errors: {} },
            }));
          }
          const message = isCreate
            ? 'Welcome Message Created Successfully'
            : 'Welcome Message updated successfully';
          notificationManager(success, message);
          if (typeof onSave === 'function') {
            onSave(updated, true);
          }
        } else {
          throw new Error('Update failed on server');
        }
        if (isCreate && tabValue === 1) {
          setIsTemplateSaved(true);
          setTabValue(2);
          updateDrawerWidth(2);
          return;
        }
        if (isCreate) {
          setTemplateEditing(true);
          if (tabValue === 2) {
            onClose();
          }
        } else {
          setTemplateEditing(false);
        }
      } catch (err) {
        console.error('Update Error:', err);
        notificationManager(
          danger,
          err?.graphQLErrors?.[0]?.message || 'Failed to update Welcome Message',
        );
      }
    } else if (!isCreate && !templateEditing) {
      setTemplateEditing(true);
    } else {
      setTabValue(tabValue + 1);
      updateDrawerWidth(tabValue + 1);
    }
  };

  const handleSave = (data) => {
    console.log(data);
    setFormData((prev) => ({
      ...prev,
      template: {
        ...prev.template,
        value: data,
        errors: {},
      },
    }));
    handleEditSave(data);
  };

  return (
    <div className='category-create' style={{ width: drawerWidth }}>
      {tabValue === 1 ? (

        <div className='template d-flex flex-column' style={{ height: '100%' }}>
          <div className='row w-100 d-flex mt-3 form-scroll justify-content-between'>
            <div className='col-md-3 mx-5 d-flex align-items-center'>
              <span className='page-title'>
                {isCreate ? 'Create Template' : templateEditing ? 'Edit Template' : 'View Template'}
              </span>

              {templateEditing && <div className='separator-line'></div>}
              {templateEditing && (
                <div className='undo-redo-container'>
                  <img
                    src={undoIcon}
                    onClick={() => templateRef.current?.handleUndo()}
                    alt='Undo'
                    className='undo-icon'
                    style={{
                      cursor: templateRef.current?.undoStack?.length ? 'pointer' : 'pointer',
                    }}
                  />
                  <img
                    src={redoIcon}
                    onClick={() => templateRef.current?.handleRedo()}
                    alt='Redo'
                    className='redo-icon'
                    style={{
                      cursor: templateRef.current?.redoStack.length ? 'pointer' : 'default',
                    }}
                  />
                </div>
              )}
            </div>
            <div className='col-md-4 mx-8 d-flex justify-content-end align-items-center'>
              <Tabs
                value={tabValue}
                variant='fullWidth'
                onChange={handleTabChange}
                indicatorColor='primary'
                textColor='primary'
                className='property-tabs'
                aria-label='message-tabs'
              >
                <Tab
                  label='Details'
                  disabled={
                    (isCreate && isTabInValid) || (!isCreate && templateEditing && tabValue !== 0)
                  }
                />
                <Tab
                  label='Template'
                  disabled={
                    (isCreate && isTabInValid) || (!isCreate && templateEditing && tabValue !== 1)
                  }
                />
                <Tab
                  label='Audience'
                  disabled={
                    (isCreate && !isTemplateSaved) ||
                    (!isCreate && templateEditing && tabValue !== 2)
                  }
                />
              </Tabs>
              <div className='col-md-2 mx-8 d-flex justify-content-end align-items-center'>
                <IconButton aria-label='cancel' onClick={onClose}>
                  <CancelIcon />
                </IconButton>
              </div>
            </div>
          </div>

          <WelcomeMessageTemplate
            formData={formData}
            handleInputChange={handleInputChange}
            isReadOnly={templateEditing || isCreate ? false : true}
            templateEditing={templateEditing}
            setTemplateEditing={setTemplateEditing}
            onTemplateSave={handleSave}
            isViewMode={isCreate ? false : true}
            setFormData={setFormData}
            ref={templateRef}
            isCreate={isCreate}
          />
        </div>
      ) : (
        // Details or Audience Tabs
        <div className='container pt-4 d-flex flex-column' style={{ height: '100%' }}>
          <div className='row px-3 w-100 d-flex mt-3 mx-1 form-scroll align-items-center'>
            <div className='col-md-5'>
              <span className='page-title'>
                {templateEditing ? (isCreate ? 'Create Message' : 'Edit Message') : 'View Message'}
              </span>
            </div>
            <div className='col-md-5'>
              <Tabs
                value={tabValue}
                variant='fullWidth'
                onChange={handleTabChange}
                indicatorColor='primary'
                textColor='primary'
                className='property-tabs'
                aria-label='message-tabs'
              >
                <Tab
                  label='Details'
                  disabled={
                    (isCreate && isTabInValid) || (!isCreate && templateEditing && tabValue !== 0)
                  }
                />
                <Tab
                  label='Template'
                  disabled={
                    (isCreate && isTabInValid) || (!isCreate && templateEditing && tabValue !== 1)
                  }
                />
                <Tab
                  label='Audience'
                  disabled={
                    (isCreate && !isTemplateSaved) ||
                    (!isCreate && templateEditing && tabValue !== 2)
                  }
                />
              </Tabs>
            </div>
            <div className='col-md-2 d-flex justify-content-end align-items-center'>
              <IconButton aria-label='cancel' onClick={onClose}>
                <CancelIcon />
              </IconButton>
            </div>
          </div>

          <div className='flex-grow-1 overflow-auto px-3'>
            {/* Details Tab */}
            {tabValue === 0 && (
              <div className='row w-100 d-flex px-3'>
                {templateEditing || isCreate ? (
                  <WelcomeMessageDetails
                    formData={formData}
                    handleInputChange={handleDetailFieldChange}
                  />
                ) : (
                  <>
                    <div className='col-md-6 mb-3 mt-5'>
                      <strong>Title</strong>
                      <p>{formData?.title?.value || '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3 mt-5'>
                      <strong>Devices</strong>
                      <p>
                        {Array.isArray(formData?.device?.value)
                          ? formData.device.value.join(', ')
                          : '-'}
                      </p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <strong>Active</strong>
                      <p>{formData?.isActive?.value ? 'Yes' : 'No'}</p>
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Audience Tab */}
            {tabValue === 2 && (
              <Audience
                formData={formData}
                handleInputChange={handleInputChange}
                originalRoomList={originalRoomList?.filter((room) => {
                  const roomType = originalRoomTypeList?.find((rt) => rt.id === room.roomTypeId);
                  return roomType ? roomType.isActive : true;
                })}
                originalRoomTypeList={originalRoomTypeList?.filter((rt) => rt.isActive)}
                originalFloorList={originalFloorList}
                isReadOnly={!templateEditing}
                setFormData={setFormData}
                hotelId={hotelId}
                language={language || 'en'}
                message={message}
              />
            )}
          </div>

          {!isCreate && (tabValue === 0 || tabValue === 2) && (
            <div className='row w-100 px-3 mb-4 d-flex justify-content-between align-items-center'>
              <div className='col-auto'>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleEditSave}
                  disabled={templateEditing && isTabInValid}
                >
                  {templateEditing ? 'Save' : 'Edit'}
                </Button>
              </div>
            </div>
          )}
          {isCreate && (
            <div className='row px-3 mb-4'>
              <div className='col-auto'>
                <Button
                  variant='contained'
                  color='primary'
                  className='me-3'
                  size='large'
                  disabled={isTabInValid}
                  onClick={handleEditSave}
                >
                  {tabValue === 2 ? 'Add' : 'Next'}
                </Button>

                <Button variant='outlined' color='secondary' size='large' onClick={onClose}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageCreateView;
